import axios, { AxiosResponse } from 'axios'
import {
  TipoUnidadeDtoCreate,
  TipoUnidadeDtoUpdate,
  TipoUnidadeList,
  TipoUnidadeView,
  UnidadeDtoCreate,
  UnidadeDtoUpdate,
  UnidadeLimiteStatus,
  UnidadeLimiteStatusResponse,
  UnidadesQueryResponse,
  UnidadeView,
} from './_models'
import {
  EnderecoDtoCreate,
  EnderecoDtoUpdate,
  EnderecoList,
  EnderecoView,
} from '../../../empresa/empresas-list/core/_models'
import { ContatoList } from '../../../../../../_metronic/helpers'
import { EsusChecklistResponseDto, EsusConfiguracaoDto, EsusConfiguracaoView } from './_esusModels'

const API_URL = process.env.REACT_APP_API_URL
const UNIDADE_URL = `${API_URL}/empresas/unidade`
const UNIDADE_TIPOS_URL = `${API_URL}/unidade/tipos`

/**
 * Busca todas as unidades de empresas
 * @param query string
 * @returns UnidadesQueryResponse
 */
export const getUnidades = (query: string): Promise<UnidadesQueryResponse> => {
  return axios
    .get(`${UNIDADE_URL}?${query}`)
    .then((resp: AxiosResponse<UnidadesQueryResponse>) => resp.data)
}

/**
 * Busca uma unidade
 * @param unidadeId Identificador da Unidade
 * @returns UnidadeView
 */
export const getUnidadeById = (unidadeId: number): Promise<UnidadeView> => {
  return axios
    .get(`${UNIDADE_URL}/${unidadeId}`)
    .then((resp: AxiosResponse<UnidadeView>) => resp.data)
}

/**
 * Altera uma unidade da empresa
 * @param unidadeId Identificador da Unidade
 * @param data UnidadeDtoUpdate
 * @returns UnidadeView
 */
export const updateUnidade = (unidadeId: number, data: UnidadeDtoUpdate): Promise<UnidadeView> => {
  return axios
    .put(`${UNIDADE_URL}/${unidadeId}`, data)
    .then((resp: AxiosResponse<UnidadeView>) => resp.data)
}

/**
 * Cadastra uma unidade da empresa
 * @param empresaId Identificador da Empresa
 * @param data UnidadeDtoCreate
 * @returns UnidadeView
 */
export const createUnidade = (empresaId: number, data: UnidadeDtoCreate): Promise<UnidadeView> => {
  return axios
    .post(`${UNIDADE_URL}/${empresaId}`, data)
    .then((resp: AxiosResponse<UnidadeView>) => resp.data)
}

/**
 * Remove uma unidade da empresa
 * @param unidadeId Identificador da Unidade
 * @returns Void
 */
export const deleteUnidade = (unidadeId: number): Promise<void> => {
  return axios.delete(`${UNIDADE_URL}/${unidadeId}`)
}

/**
 * Busca endereços da unidade
 * @param unidadeId Identificador da Unidade
 * @returns EnderecoList[]
 */
export const getUnidadeEnderecos = (unidadeId: number): Promise<EnderecoList[]> => {
  return axios
    .get(`${UNIDADE_URL}/endereco/${unidadeId}`)
    .then((resp: AxiosResponse<EnderecoList[]>) => resp.data)
}

/**
 * Altera um endereço
 * @param unidadeId Identificador da Unidade
 * @param data EnderecoDtoUpdate
 * @returns EnderecoView
 */
export const updateUnidadeEndereco = (
  unidadeId: number,
  data: EnderecoDtoUpdate
): Promise<EnderecoView> => {
  return axios
    .put(`${UNIDADE_URL}/endereco/${unidadeId}`, data)
    .then((resp: AxiosResponse<EnderecoView>) => resp.data)
}

/**
 * Cadastra um endereço
 * @param unidadeId Identificador da Unidade
 * @param data EnderecoDtoCreate
 * @returns EnderecoView
 */
export const createUnidadeEndereco = (
  unidadeId: number,
  data: EnderecoDtoCreate
): Promise<EnderecoView> => {
  return axios
    .post(`${UNIDADE_URL}/endereco/${unidadeId}`, data)
    .then((resp: AxiosResponse<EnderecoView>) => resp.data)
}

/**
 * Busca contatos da unidade
 * @param unidadeId Identificador da Unidade
 * @returns ContatoList[]
 */
export const getContatosByUnidade = (unidadeId: number): Promise<ContatoList[]> => {
  return axios
    .get(`${UNIDADE_URL}/contato/${unidadeId}`)
    .then((resp: AxiosResponse<ContatoList[]>) => resp.data)
}

/**
 * Busca contato de agendamentos da unidade
 * @param unidadeId Identificador da Unidade
 * @returns ContatoList
 */
export const getContatosOfAgendamentosByUnidade = (unidadeId: number): Promise<ContatoList> => {
  return axios
    .get(`${UNIDADE_URL}/contato/agendamento/${unidadeId}`)
    .then((resp: AxiosResponse<ContatoList>) => resp.data)
}

/**
 * Busca endereços de atendimento
 * @param unidadeId Identificador da Unidade
 * @returns EnderecoList[]
 */
export const getUnidadeEnderecoAtendimento = (unidadeId: number): Promise<EnderecoList[]> => {
  return axios
    .get(`${UNIDADE_URL}/endereco/atendimento/${unidadeId}`)
    .then((resp: AxiosResponse<EnderecoList[]>) => resp.data)
}

/**
 * Busca todos os tipos de unidade
 * @returns TipoUnidadeList[]
 */
export const getTiposUnidades = (): Promise<TipoUnidadeList[]> => {
  return axios
    .get(`${UNIDADE_TIPOS_URL}`)
    .then((resp: AxiosResponse<TipoUnidadeList[]>) => resp.data)
}

/**
 * Busca um tipo de unidade
 * @param tipoUnidadeId Identificador do tipo da unidade
 * @returns TipoUnidadeView
 */
export const getTiposUnidadesById = (tipoUnidadeId: number): Promise<TipoUnidadeView> => {
  return axios
    .get(`${UNIDADE_TIPOS_URL}/${tipoUnidadeId}`)
    .then((resp: AxiosResponse<TipoUnidadeView>) => resp.data)
}

/**
 * Cria um tipo de unidade
 * @param data TipoUnidadeDtoCreate
 * @returns TipoUnidadeView
 */
export const createTipoUnidade = (data: TipoUnidadeDtoCreate): Promise<TipoUnidadeView> => {
  return axios
    .post(`${UNIDADE_TIPOS_URL}`, data)
    .then((resp: AxiosResponse<TipoUnidadeView>) => resp.data)
}

/**
 * Altera um tipo de unidade
 * @param data TipoUnidadeDtoUpdate
 * @returns TipoUnidadeView
 */
export const updateTipoUnidade = (data: TipoUnidadeDtoUpdate): Promise<TipoUnidadeView> => {
  return axios
    .put(`${UNIDADE_TIPOS_URL}`, data)
    .then((resp: AxiosResponse<TipoUnidadeView>) => resp.data)
}

/**
 * Remove um tipo de unidade
 * @param tipoUnidadeId Identificador do tipo da unidade
 * @returns void
 */
export const deleteTipoUnidade = (tipoUnidadeId: number): Promise<void> => {
  return axios
    .delete(`${UNIDADE_TIPOS_URL}/${tipoUnidadeId}`)
    .then((resp: AxiosResponse<void>) => resp.data)
}

/**
 * Busca quantidade e limite de unidades ativas da empresa
 * @param empresaId Identificador de empresa
 * @returns LimiteUnidades
 */
export const getLimiteUnidades = (empresaId: number): Promise<UnidadeLimiteStatus> => {
  return axios
    .get(`${UNIDADE_URL}/count/${empresaId}`)
    .then((resp: AxiosResponse<UnidadeLimiteStatusResponse>) => resp.data.data)
}

export const getEsusConfigEmpresa = (empresaId: number): Promise<EsusConfiguracaoView> => {
  return axios
    .get(`${API_URL}/empresas/${empresaId}/esus-configuracao`)
    .then((resp: AxiosResponse<EsusConfiguracaoView>) => resp.data)
}

export const updateEsusConfigEmpresa = (
  empresaId: number,
  data: EsusConfiguracaoDto
): Promise<EsusConfiguracaoView> => {
  return axios
    .put(`${API_URL}/empresas/${empresaId}/esus-configuracao`, data)
    .then((resp: AxiosResponse<EsusConfiguracaoView>) => resp.data)
}

export const getEsusConfigUnidade = (
  empresaId: number,
  unidadeId: number
): Promise<EsusConfiguracaoView> => {
  return axios
    .get(`${API_URL}/empresas/${empresaId}/esus-configuracao/unidades/${unidadeId}`)
    .then((resp: AxiosResponse<EsusConfiguracaoView>) => resp.data)
}

export const updateEsusConfigUnidade = (
  empresaId: number,
  unidadeId: number,
  data: EsusConfiguracaoDto
): Promise<EsusConfiguracaoView> => {
  return axios
    .put(`${API_URL}/empresas/${empresaId}/esus-configuracao/unidades/${unidadeId}`, data)
    .then((resp: AxiosResponse<EsusConfiguracaoView>) => resp.data)
}

export const ativarEsusIntegracao = (
  empresaId: number,
  unidadeId: number
): Promise<EsusConfiguracaoView> => {
  return axios
    .put(`${API_URL}/empresas/${empresaId}/esus-configuracao/unidades/${unidadeId}/ativacao`)
    .then((resp: AxiosResponse<EsusConfiguracaoView>) => resp.data)
}

export const desativarEsusIntegracao = (
  empresaId: number,
  unidadeId: number
): Promise<EsusConfiguracaoView> => {
  return axios
    .delete(`${API_URL}/empresas/${empresaId}/esus-configuracao/unidades/${unidadeId}/ativacao`)
    .then((resp: AxiosResponse<EsusConfiguracaoView>) => resp.data)
}

export const getEsusChecklist = (
  empresaId: number,
  unidadeId: number
): Promise<EsusChecklistResponseDto> => {
  return axios
    .get(`${API_URL}/empresas/${empresaId}/esus-configuracao/unidades/${unidadeId}/checklist`)
    .then((resp: AxiosResponse<EsusChecklistResponseDto>) => resp.data)
}
